import { BasketModifierSelection } from "../../../store/slices/basketSlice";
import { IModifierGroup, IModifierItem } from "../types/Menu";

// Выбранные модификаторы: groupId -> itemId -> количество.
export type ModifierState = Record<string, Record<string, number>>;

export const getItemPrice = (item?: IModifierItem) => Number(item?.price ?? 0);

// itemGroupId и sku бэк заполняет не всегда (у комбо оба пустые), тогда группу
// различает только её позиция в массиве. Поэтому исходный индекс нельзя терять
// при фильтрации групп — иначе ключи разъедутся с состоянием выбора.
export const getGroupId = (group: IModifierGroup, index: number) =>
  group.itemGroupId || group.sku || String(index);

// maxQuantity: 0 бэк присылает в значении «без ограничения», а не «нельзя взять
// ни одной». У комбо все напитки приходят с max 0, при этом сама группа требует
// ровно один — считать ноль потолком значит заблокировать выбор, которого от
// пользователя как раз и ждут. Ограничивает в таком случае максимум группы.
export const resolveMaxQuantity = (max?: number | null) =>
  max == null || max <= 0 ? Infinity : max;

// Позиция зафиксирована, когда минимум совпадает с максимумом: и «+», и «−»
// у неё всегда заблокированы, менять нечего. Нулевой максимум сюда не входит —
// это не «зафиксирована на нуле», а «ограничений нет», см. resolveMaxQuantity.
export const isFixedItem = (item: IModifierItem) => {
  const min = item.restrictions?.minQuantity;
  const max = item.restrictions?.maxQuantity;

  return min != null && max != null && min === max && max > 0;
};

// Группа, где зафиксированы все позиции, — это не «Дополнительно», а состав
// набора (комбо приходит группой из трёх позиций по min=max=1). Выбирать в ней
// нечего, поэтому на детальной её не рисуем, а с карточки такой товар кладём
// в корзину сразу, не заходя внутрь. В заказ она всё равно уезжает целиком:
// buildModifierPayload обходит все группы, а не только показанные.
export const isChoosableGroup = (group: IModifierGroup) => {
  const items = group.items ?? [];

  if (items.length === 0) {
    return false;
  }

  // Совпадение min и max у позиции — слишком слабый признак: у напитков это
  // 0 и 0, то есть «своих ограничений нет, всё решает группа», а вовсе не
  // «позиция зафиксирована». Бэк отвечает на этот вопрос прямо, и его ответу
  // мы верим больше, чем догадке по числам.
  if (group.childModifiersHaveMinMaxRestrictions === false) {
    return true;
  }

  return !items.every(isFixedItem);
};

// Группа-исключение: её позиции не добавляют к блюду, а убирают из состава
// ("Уберите ингредиенты"). Приходит обычной группой модификаторов — отдельного
// признака в ответе нет, отличается только названием, поэтому смотрим на него
// на всех трёх языках. Выбор в такой группе работает как везде: выбранная
// позиция уезжает в заказ, просто означает "без этого".
const REMOVAL_GROUP_PATTERN = /убер|убрать|olib tashla|chiqarib|remove/i;

export const isRemovalGroupName = (name?: string | null) =>
  REMOVAL_GROUP_PATTERN.test(name ?? "");

export const isRemovalGroup = (group: IModifierGroup) =>
  isRemovalGroupName(group.name);

// Группы, ради которых есть смысл открывать детальную. Индекс — из исходного
// массива, см. getGroupId.
export const getChoosableGroups = (groups?: IModifierGroup[] | null) =>
  (groups ?? [])
    .map((group, index) => ({ group, index }))
    .filter(({ group }) => isChoosableGroup(group));

// Стартовое количество позиции.
//
// byDefault бэк присылает не всегда, и для зафиксированной позиции (min == max)
// его отсутствие означало ноль. А выбрать её руками негде: группа из одних
// зафиксированных позиций — это состав набора, её на детальной не рисуют. В
// итоге проверка минимума по группе не проходила никогда и «В корзину»
// оставалась заблокированной, без единого намёка почему. У такой позиции
// количество не выбирают — оно и есть min, поэтому берём максимум из двух.
export const getDefaultQuantity = (item: IModifierItem) => {
  const byDefault = item.restrictions?.byDefault ?? 0;
  const required = isFixedItem(item)
    ? (item.restrictions?.minQuantity ?? 0)
    : 0;

  return Math.max(byDefault, required, 0);
};

export const getDefaultSelections = (groups?: IModifierGroup[] | null) =>
  (groups ?? []).reduce<ModifierState>((acc, group, index) => {
    const groupId = getGroupId(group, index);
    const items = group.items ?? [];

    acc[groupId] = items.reduce<Record<string, number>>((itemAcc, item) => {
      itemAcc[item.itemId] = getDefaultQuantity(item);
      return itemAcc;
    }, {});

    return acc;
  }, {});

// Порядок выбора внутри группы: itemId в том порядке, в котором их включали.
// Нужен для вытеснения по FIFO, когда в группе выбирают больше max_quantity.
export type ModifierOrder = Record<string, string[]>;

export const getGroupMax = (group: IModifierGroup) =>
  resolveMaxQuantity(group.restrictions?.maxQuantity);

export const getGroupSelectedCount = (groupState?: Record<string, number>) =>
  Object.values(groupState ?? {}).reduce((sum, quantity) => sum + quantity, 0);

// Группа с min=max=1 — это радиокнопки, там лимит и так очевиден по вёрстке.
export const isSingleChoiceGroup = (group: IModifierGroup) =>
  (group.restrictions?.minQuantity ?? 0) === 1 &&
  (group.restrictions?.maxQuantity ?? 0) === 1;

// Лимит показываем, только когда он реально что-то ограничивает: позиций в
// группе больше, чем разрешено выбрать. null — подсказка не нужна.
export const getGroupLimit = (group: IModifierGroup) => {
  const max = group.restrictions?.maxQuantity;
  const itemsCount = (group.items ?? []).length;

  if (max == null || !Number.isFinite(max) || max <= 0) {
    return null;
  }

  return max < itemsCount && !isSingleChoiceGroup(group) ? max : null;
};

export const getDefaultOrder = (groups?: IModifierGroup[] | null) =>
  (groups ?? []).reduce<ModifierOrder>((acc, group, index) => {
    acc[getGroupId(group, index)] = (group.items ?? [])
      .filter((item) => getDefaultQuantity(item) > 0)
      .map((item) => item.itemId);

    return acc;
  }, {});

export type ModifierSelection = {
  quantities: ModifierState;
  order: ModifierOrder;
};

// Меняет количество одной позиции и возвращает новый выбор целиком.
//
// Лимит группы ничего не блокирует: если выбор перестаёт влезать в
// max_quantity, гасим самые ранние из уже выбранных позиций (FIFO), пока сумма
// не вернётся в лимит. Поэтому вместе с количествами тащим и порядок выбора —
// по ключам объекта его не восстановить: там лежат и невыбранные позиции.
export const applyGroupSelection = (
  { quantities, order }: ModifierSelection,
  group: IModifierGroup,
  groupIndex: number,
  item: IModifierItem,
  nextQuantity: number,
): ModifierSelection => {
  const groupId = getGroupId(group, groupIndex);
  const itemId = item.itemId;
  const groupState = quantities[groupId] ?? {};
  const groupOrder = order[groupId] ?? [];

  if (isSingleChoiceGroup(group)) {
    return {
      quantities: {
        ...quantities,
        [groupId]: {
          ...Object.fromEntries(Object.keys(groupState).map((key) => [key, 0])),
          [itemId]: 1,
        },
      },
      order: { ...order, [groupId]: [itemId] },
    };
  }

  const maxPerItem = resolveMaxQuantity(item.restrictions?.maxQuantity);
  const minPerItem = item.restrictions?.minQuantity ?? 0;
  const groupMax = getGroupMax(group);

  const clampedQuantity =
    nextQuantity <= 0
      ? 0
      : Math.max(minPerItem, Math.min(nextQuantity, maxPerItem));

  const nextGroupState = { ...groupState, [itemId]: clampedQuantity };
  let overflow = getGroupSelectedCount(nextGroupState) - groupMax;

  for (const candidateId of groupOrder) {
    if (overflow <= 0) {
      break;
    }

    const candidateQuantity = nextGroupState[candidateId] ?? 0;

    if (candidateId === itemId || candidateQuantity <= 0) {
      continue;
    }

    nextGroupState[candidateId] = 0;
    overflow -= candidateQuantity;
  }

  // Вытеснять больше некого, а лимит всё ещё превышен: у самой позиции
  // max_quantity больше, чем разрешает группа. Упираемся в лимит группы.
  if (overflow > 0) {
    nextGroupState[itemId] = Math.max(0, clampedQuantity - overflow);
  }

  // Позиция, которую уже выбирали, остаётся на своём месте в очереди: FIFO
  // считает по первому включению, а не по последнему изменению счётчика.
  const keptOrder = groupOrder.filter((id) => (nextGroupState[id] ?? 0) > 0);
  const nextOrder =
    (nextGroupState[itemId] ?? 0) > 0 && !keptOrder.includes(itemId)
      ? [...keptOrder, itemId]
      : keptOrder;

  return {
    quantities: { ...quantities, [groupId]: nextGroupState },
    order: { ...order, [groupId]: nextOrder },
  };
};

// То, что уходит в корзину и дальше в позиции заказа.
export const buildModifierPayload = (
  groups: IModifierGroup[],
  selections: ModifierState,
): BasketModifierSelection[] =>
  groups.flatMap((group, groupIndex) =>
    (group.items ?? []).flatMap((item) => {
      const groupId = getGroupId(group, groupIndex);
      const quantity = selections[groupId]?.[item.itemId] ?? 0;

      if (quantity <= 0) {
        return [];
      }

      return [
        {
          groupId: group.itemGroupId,
          groupSku: group.sku,
          groupName: group.name,
          itemId: item.itemId,
          itemSku: item.sku,
          itemName: item.name,
          quantity,
          price: getItemPrice(item),
          item,
        },
      ];
    }),
  );

export const getModifierTotal = (modifiers: BasketModifierSelection[]) =>
  modifiers.reduce((total, item) => total + item.price * item.quantity, 0);

// Одно и то же блюдо с разными модификаторами — разные строки корзины, поэтому
// ключ включает выбор. Строит его и детальная, и карточка: разойдись они хоть
// в порядке сортировки, счётчик на карточке перестал бы находить свою строку.
export const buildCartKey = (productId: number, selections: ModifierState) => {
  const normalized = Object.entries(selections)
    .flatMap(([groupId, items]) =>
      Object.entries(items)
        .filter(([, quantity]) => quantity > 0)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([itemId, quantity]) => `${groupId}:${itemId}:${quantity}`),
    )
    .sort()
    .join("|");

  return normalized ? `${productId}__${normalized}` : String(productId);
};
